import { useState } from 'react';

import type { CashAccountBalance } from '@/api/cash';
import { CashAccountKindValues, type CashAccountKind } from '@/api/generated/enums';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Button, Card, Notice, PageHeading } from '@/components/PageHeading';
import { Field, controlClass, selectClass } from '@/components/Field';
import { formatAmount } from '@core/format/money';
import { humanizeEnum } from '@core/format/text';

export interface CashAccountDraft {
  name: string;
  kind: CashAccountKind;
  bankReference: string | null;
  notes: string | null;
  isActive: boolean;
  /** Till and petty cash only; null clears it. */
  floatToKeep: number | null;
}

/**
 * Opening an account, and editing one (spec Section 18).
 *
 * <p>
 * <strong>A page rather than the inline rename it replaces</strong>, per the rule settled
 * 2026-09-08: a screen that writes a record is a page. The rename was the whole of account
 * maintenance while a branch could hold only one account of each kind; it is now one field among
 * four, and a text box wedged into a balance card was never going to hold the rest.
 * </p>
 *
 * <p>
 * <strong>The kind is fixed once the account exists.</strong> It is what the resolver routes on, so
 * changing it would re-home every posting already made — a till that became a bank account would
 * take a day's cash off the drawer somebody counts.
 * </p>
 */
export function CashAccountPage({
  account,
  busy = false,
  onCancel,
  onSave,
}: {
  /** The account being edited, or undefined when opening a new one. */
  account?: CashAccountBalance | undefined;
  busy?: boolean;
  onCancel: () => void;
  onSave: (draft: CashAccountDraft) => void;
}) {
  const firstField = useAutoFocusFirstField();

  const [name, setName] = useState(account?.name ?? '');
  const [kind, setKind] = useState<CashAccountKind>(account?.kind ?? 'Till');
  const [bankReference, setBankReference] = useState(account?.bankReference ?? '');
  const [notes, setNotes] = useState(account?.notes ?? '');
  const [isActive, setIsActive] = useState(account?.isActive ?? true);
  const [floatToKeep, setFloatToKeep] = useState(
    account?.floatToKeep == null ? '' : String(account.floatToKeep),
  );

  const editing = account !== undefined;

  // Mirrored from the server, which refuses it: the resolver posts to the default by definition, so
  // retiring one would fail the next payment at the counter. Naming another default first is the
  // migration path, and saying that here is what stops it reading as an arbitrary refusal.
  const defaultCannotRetire = editing && account.isDefault;

  // A bank has no drawer to leave change in, and the server refuses a float on one.
  const keepsFloat = kind !== 'Bank';
  const floatValue = floatToKeep.trim() === '' ? null : Number(floatToKeep);
  const floatOk = floatValue === null || (Number.isFinite(floatValue) && floatValue >= 0);

  const ready = name.trim() !== '' && floatOk;

  return (
    <div className="flex flex-col gap-3 lg:gap-4" {...enterKeyScope}>
      <PageHeading
        title={editing ? 'Edit account' : 'Open an account'}
        subtitle={
          editing
            ? 'What the shop calls it, and whether it still takes money'
            : 'A second till, a bank account, the petty-cash float'
        }
        actions={
          <>
            <Button onClick={onCancel} disabled={busy} shortcut="back">
              Cancel
            </Button>
            <Button
              variant="primary"
              icon="icon icon-check"
              data-enter-key-commit
              shortcut="save"
              loading={busy}
              disabled={!ready || busy}
              onClick={() =>
                onSave({
                  name: name.trim(),
                  kind,
                  bankReference: bankReference.trim() || null,
                  notes: notes.trim() || null,
                  isActive,
                  floatToKeep: keepsFloat ? floatValue : null,
                })
              }
            >
              {editing ? 'Save' : 'Open the account'}
            </Button>
          </>
        }
      />

      <div className="grid gap-3 lg:grid-cols-2 lg:gap-4">
        <Card>
          <p className="mb-2.5 text-sm font-semibold text-gray-900">What it is</p>

          <div className="flex flex-col gap-3">
            <Field
              label="Name"
              required
              hint="What whoever counts it calls it — “Counter”, “HDFC current”."
            >
              <input
                ref={firstField}
                type="text"
                disabled={busy}
                value={name}
                onChange={(event) => setName(event.target.value)}
                className={controlClass}
              />
            </Field>

            <Field
              label="Kind"
              required
              hint={
                editing
                  ? 'Fixed once money has been posted — it is what decides where a payment lands.'
                  : 'Cash lands in the till, everything else in the bank. Petty cash is topped up deliberately.'
              }
            >
              <select
                disabled={busy || editing}
                value={kind}
                onChange={(event) => setKind(event.target.value as CashAccountKind)}
                className={selectClass}
              >
                {CashAccountKindValues.map((option) => (
                  <option key={option} value={option}>
                    {humanizeEnum(option)}
                  </option>
                ))}
              </select>
            </Field>

            <Field
              label="Bank reference"
              hint="What you recognise it by on a statement — “HDFC current ••4471”."
            >
              <input
                type="text"
                disabled={busy}
                value={bankReference}
                onChange={(event) => setBankReference(event.target.value)}
                className={controlClass}
              />
            </Field>

            {keepsFloat && (
              <Field
                label={kind === 'PettyCash' ? 'Imprest' : 'Float to keep'}
                hint={
                  kind === 'PettyCash'
                    ? 'What petty cash is topped back up to. Blank for none.'
                    : 'Change left in the drawer at close; Move money offers to bank the rest. Blank for none.'
                }
              >
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  disabled={busy}
                  value={floatToKeep}
                  onChange={(event) => setFloatToKeep(event.target.value)}
                  onWheel={(event) => event.currentTarget.blur()}
                  className={controlClass}
                />
              </Field>
            )}

            <Field label="Notes">
              <textarea
                rows={2}
                disabled={busy}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                className={controlClass}
              />
            </Field>
          </div>
        </Card>

        {editing && (
          <Card>
            <p className="mb-2.5 text-sm font-semibold text-gray-900">
              Whether it still takes money
            </p>

            <Field
              label="In use"
              hint="Retiring it stops new postings and counting. It stays on the cash book, and its balance stays in the total."
            >
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  disabled={busy || defaultCannotRetire}
                  checked={isActive}
                  onChange={(event) => setIsActive(event.target.checked)}
                />
                <span className="text-[length:var(--scale-font-size)] text-gray-900">
                  This account is in use
                </span>
              </label>
            </Field>

            {defaultCannotRetire && (
              <Notice tone="info" title="This is where the branch posts">
                Money of its kind lands here, so it cannot be retired while that is true. Make
                another account the default first — the cash book offers that on every other
                account.
              </Notice>
            )}

            {!isActive && account.closing !== 0 && (
              <Notice tone="warning" title="It still holds money">
                {formatAmount(account.closing)} stays in the total after this is retired, because
                there is nowhere yet to move it to. Retiring only stops new money going in.
              </Notice>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
